import React, { useMemo, useState } from 'react';
import { Database, CheckCircle, AlertTriangle, Layers, GitBranch, Tag, Zap, BarChart3, FileJson, ChevronDown, ChevronRight, AlertOctagon } from 'lucide-react';
import { validateSeedCoverage, validateSeedQuestionsLocal, getAllSeedQuestions, getSeedQuestionTypeDistribution, SEED_COLLECTIONS, getSeedCollection } from '../../../data/seedConsolidator';
import { TAXONOMY, FAMILIES_BY_MODULE } from '../../../config/taxonomyRegistry';
import { MODULES } from '../../../data/modules';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { ModuleId } from '../../../types';
export function SeedStatus() {
  const [expandedModule, setExpandedModule] = useState<ModuleId | null>(null);
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);
  const allQuestions = useMemo(() => getAllSeedQuestions(), []);
  const coverageReport = useMemo(() => validateSeedCoverage(), []);
  const validationReport = useMemo(() => validateSeedQuestionsLocal(), []);
  const typeDistribution = useMemo(() => getSeedQuestionTypeDistribution(), []);
  const coveragePercent = Math.round(coverageReport.coveredFamilies / coverageReport.totalFamilies * 100);
  return <div className="space-y-8" data-id="element-1952">
      {/* Top Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1953">
        <StatCard label="Total Seed Questions" value={allQuestions.length} icon={Database} color="indigo" data-id="element-1954" />

        <StatCard label="Taxonomy Coverage" value={`${coveragePercent}%`} icon={GitBranch} color={coveragePercent === 100 ? 'emerald' : 'amber'} data-id="element-1955" />

        <StatCard label="Family Coverage" value={`${coverageReport.coveredFamilies}/${coverageReport.totalFamilies}`} icon={Tag} color="blue" data-id="element-1956" />

        <StatCard label="Question Types" value={Object.keys(typeDistribution).length} icon={Layers} color="purple" data-id="element-1957" />

      </div>

      {/* Coverage Status Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1958">
        <Card className="p-6 lg:col-span-2" data-id="element-1959">
          <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-1960">
            <CheckCircle className="w-5 h-5 text-emerald-600" data-id="element-1961" />
            Coverage Status
          </h3>

          <div className="mb-8" data-id="element-1962">
            <div className="flex justify-between items-end mb-2" data-id="element-1963">
              <span className="text-sm font-medium text-slate-600" data-id="element-1964">
                Overall Progress
              </span>
              <span className={`text-2xl font-bold ${coveragePercent === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-1965">

                {coveragePercent}%
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden" data-id="element-1966">
              <div className={`h-full transition-all duration-500 ${coveragePercent === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{
              width: `${coveragePercent}%`
            }} data-id="element-1967" />

            </div>
          </div>

          {coverageReport.missingFamilies.length > 0 ? <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-id="element-1968">
              <h4 className="text-sm font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-1969">
                <AlertTriangle className="w-4 h-4" data-id="element-1970" />
                Missing Families ({coverageReport.missingFamilies.length})
              </h4>
              <div className="space-y-2 max-h-60 overflow-y-auto pr-2" data-id="element-1971">
                {coverageReport.missingFamilies.map(id => <div key={id} className="flex items-center justify-between bg-white p-2 rounded border border-amber-100 text-sm" data-id="element-1972">

                    <span className="font-mono text-amber-900" data-id="element-1973">{id}</span>
                  </div>)}
              </div>
            </div> : <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 flex items-center gap-3" data-id="element-1974">
              <div className="bg-emerald-100 p-2 rounded-full" data-id="element-1975">
                <CheckCircle className="w-5 h-5 text-emerald-600" data-id="element-1976" />
              </div>
              <div data-id="element-1977">
                <div className="font-bold text-emerald-900" data-id="element-1978">
                  100% Coverage Achieved
                </div>
                <div className="text-sm text-emerald-700" data-id="element-1979">
                  All {coverageReport.totalFamilies} families from the taxonomy
                  have a corresponding seed question.
                </div>
              </div>
            </div>}
        </Card>

        {/* Question Type Distribution */}
        <Card className="p-6" data-id="element-1980">
          <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-1981">
            <Layers className="w-5 h-5 text-purple-600" data-id="element-1982" />
            Question Types
          </h3>

          <div className="space-y-4" data-id="element-1983">
            <TypeRow label="Multiple Choice" count={typeDistribution['multiple_choice'] || 0} required data-id="element-1984" />

            <div className="h-px bg-slate-100 my-2" data-id="element-1985" />
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1986">
              Interactive Types
            </div>

            <TypeRow label="Klok (Clock)" count={typeDistribution['clock'] || 0} required data-id="element-1987" />

            <TypeRow label="Geld (Money)" count={typeDistribution['money'] || 0} required data-id="element-1988" />

            <TypeRow label="Cijferend Optellen" count={typeDistribution['cijferend_optellen'] || 0} required data-id="element-1989" />

            <TypeRow label="Invulvraag" count={typeDistribution['fill_in'] || 0} required data-id="element-1990" />

            <TypeRow label="Tabel Lezen" count={typeDistribution['table_reading'] || 0} required data-id="element-1991" />

          </div>
        </Card>
      </div>

      {/* Per-Module Breakdown */}
      <div data-id="element-1992">
        <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1993">
          <BarChart3 className="w-5 h-5 text-blue-600" data-id="element-1994" />
          Module Breakdown
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1995">
          {MODULES.map(module => {
          const collection = SEED_COLLECTIONS.get(module.id);
          const families = FAMILIES_BY_MODULE.get(module.id) || [];
          const questionCount = collection?.questions.length || 0;
          const familyCount = families.length;
          const coverage = familyCount > 0 ? Math.round(questionCount / familyCount * 100) : 0;
          const tiers = collection?.questions.map(q => q.tier).filter(Boolean) || [];
          const minTier = tiers.length > 0 ? Math.min(...tiers) : 0;
          const maxTier = tiers.length > 0 ? Math.max(...tiers) : 0;
          return <Card key={module.id} className="p-4 border-l-4" style={{
            borderLeftColor: getModuleColor(module.id)
          }} data-id="element-1996">

                <div className="flex justify-between items-start mb-2" data-id="element-1997">
                  <h4 className="font-bold text-slate-900" data-id="element-1998">{module.title}</h4>
                  <Badge variant="neutral" size="sm" data-id="element-1999">
                    {collection?.moduleCode}
                  </Badge>
                </div>

                <div className="space-y-3 mt-4" data-id="element-2000">
                  <div className="flex justify-between text-sm" data-id="element-2001">
                    <span className="text-slate-500" data-id="element-2002">Questions</span>
                    <span className="font-mono font-bold text-slate-700" data-id="element-2003">
                      {questionCount}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-2004">
                    <span className="text-slate-500" data-id="element-2005">Families</span>
                    <span className="font-mono font-bold text-slate-700" data-id="element-2006">
                      {familyCount}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-2007">
                    <span className="text-slate-500" data-id="element-2008">Tier Range</span>
                    <span className="font-mono font-bold text-slate-700" data-id="element-2009">
                      {minTier}-{maxTier}
                    </span>
                  </div>

                  <div className="pt-2" data-id="element-2010">
                    <div className="flex justify-between text-xs mb-1" data-id="element-2011">
                      <span className="text-slate-400" data-id="element-2012">Coverage</span>
                      <span className={coverage === 100 ? 'text-emerald-600 font-bold' : 'text-amber-600 font-bold'} data-id="element-2013">

                        {coverage}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-2014">
                      <div className={`h-full rounded-full ${coverage === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{
                    width: `${coverage}%`
                  }} data-id="element-2015" />

                    </div>
                  </div>
                </div>
              </Card>;
        })}
        </div>
      </div>

      {/* Validation Results */}
      <Card className="p-6" data-id="element-2016">
        <div className="flex items-center justify-between mb-6" data-id="element-2017">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-2018">
            <Zap className="w-5 h-5 text-amber-500" data-id="element-2019" />
            Validation Results
          </h3>
          <div className="flex gap-2" data-id="element-2020">
            <Badge variant="success" size="lg" data-id="element-2021">
              {validationReport.valid} Valid
            </Badge>
            {validationReport.invalid > 0 && <Badge variant="error" size="lg" data-id="element-2022">
                {validationReport.invalid} Invalid
              </Badge>}
          </div>
        </div>

        {validationReport.issues.length > 0 ? <div className="space-y-2" data-id="element-2023">
            {validationReport.issues.map(issue => <div key={issue.id} className="bg-rose-50 border border-rose-200 rounded-lg p-3" data-id="element-2024">

                <div className="font-mono text-sm font-bold text-rose-800 mb-1" data-id="element-2025">
                  {issue.id}
                </div>
                <ul className="list-disc list-inside text-sm text-rose-700" data-id="element-2026">
                  {issue.problems.map((p, i) => <li key={i} data-id="element-2027">{p}</li>)}
                </ul>
              </div>)}
          </div> : <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200" data-id="element-2028">
            <CheckCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-2029" />
            <p className="text-slate-500 font-medium" data-id="element-2030">
              All seed questions passed validation
            </p>
          </div>}
      </Card>
    </div>;
}
function StatCard({
  label,
  value,
  icon: Icon,
  color
}: any) {
  const colorClasses = {
    indigo: 'bg-indigo-50 text-indigo-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600'
  };
  return <Card className="p-4 flex items-center justify-between" data-id="element-2031">
      <div data-id="element-2032">
        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-2033">
          {label}
        </div>
        <div className="text-2xl font-bold text-slate-900" data-id="element-2034">{value}</div>
      </div>
      <div className={`p-3 rounded-full ${colorClasses[color as keyof typeof colorClasses]}`} data-id="element-2035">

        <Icon className="w-6 h-6" data-id="element-2036" />
      </div>
    </Card>;
}
function TypeRow({
  label,
  count,
  required
}: any) {
  return <div className="flex items-center justify-between" data-id="element-2037">
      <div className="flex items-center gap-2" data-id="element-2038">
        {count > 0 ? <CheckCircle className="w-4 h-4 text-emerald-500" data-id="element-2039" /> : required ? <AlertTriangle className="w-4 h-4 text-rose-500" data-id="element-2040" /> : <div className="w-4 h-4" data-id="element-2041" />}
        <span className="text-sm font-medium text-slate-700" data-id="element-2042">{label}</span>
      </div>
      <Badge variant={count > 0 ? 'neutral' : 'warning'} size="sm" data-id="element-2043">
        {count}
      </Badge>
    </div>;
}
function getModuleColor(moduleId: ModuleId): string {
  const colors: Record<ModuleId, string> = {
    Metriek: '#3b82f6',
    Getalbegrip: '#6366f1',
    TijdGeld: '#10b981',
    SpellingWW: '#f43f5e',
    SpellingRegel: '#f59e0b',
    Taalbegrip: '#a855f7',
    Studievaardig: '#14b8a6',
    Topo: '#06b6d4'
  };
  return colors[moduleId] || '#64748b';
}